import { useLocation, Link } from "wouter";
import {
  LayoutDashboard,
  Briefcase,
  UsersRound,
  DollarSign,
  FileText,
  Settings,
  Trophy,
  Zap,
  Sun,
  Moon,
  Clock,
  Rocket,
  SquareCheckBig,
} from "lucide-react";
import { DuckMark } from "@/components/ducks/duck-mark";
import { useAuth } from "@/hooks/use-auth";
import { useDucksClients } from "@/hooks/use-ducks";
import { useTheme } from "@/hooks/use-theme";
import { isDucksRoute } from "@/lib/ducks";
import { cn } from "@/lib/utils";

export default function Sidebar() {
  const [location] = useLocation();
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { data: ducksClients } = useDucksClients();

  const navRoutes = [
    { href: "/", label: "Dashboard", icon: LayoutDashboard },
    { href: "/clients", label: "Clients", icon: Briefcase },
    // Tasks is gated to allowlisted sidekicks (see tasks_enabled on /auth/me).
    ...(user?.tasks_enabled
      ? [{ href: "/tasks", label: "Tasks", icon: SquareCheckBig }]
      : []),
    // Only for a Sidekick with a client who has Ducks in a Row switched on.
    ...(ducksClients?.length
      ? [{ href: "/ducks", label: "Ducks in a Row", icon: DuckMark }]
      : []),
    { href: "/payments", label: "Payments", icon: DollarSign },
    { href: "/overtime-requests", label: "Overtime Requests", icon: Clock },
    { href: "/missions", label: "Epic Missions", icon: Rocket },
  ];

  const toolRoutes = [
    { href: "/legal-documents", label: "Legal Documents", icon: FileText },
    { href: "/referral-leaderboard", label: "Leaderboard", icon: Trophy },
    { href: "/community", label: "Community", icon: UsersRound },
    { href: "/settings", label: "Settings", icon: Settings },
  ];

  return (
    <aside className="hidden lg:flex lg:flex-shrink-0">
      {/* No border, no internal dividers: three rules on a 60px rail cut it into
          three stripes and made the whole thing read as chrome around the app
          rather than as one edge of it. */}
      <div className="h-full w-[60px] bg-[#111827] flex flex-col">
        {/* Logo */}
        <div className="flex justify-center py-4">
          <div className="bg-blue-600 rounded-[9px] p-[7px]">
            <Zap className="h-[18px] w-[18px] text-white" />
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 flex flex-col items-center py-2 gap-0.5 overflow-y-auto">
          {[...navRoutes, ...toolRoutes].map((route) => {
            const isActive =
              route.href === location || (route.href === "/ducks" && isDucksRoute(location));
            const Icon = route.icon;
            return (
              <Link key={route.href} href={route.href}>
                <a
                  title={route.label}
                  className={cn(
                    "w-[38px] h-[38px] flex items-center justify-center rounded-[10px] transition-colors",
                    isActive
                      ? "bg-blue-600 text-white"
                      // Warmer than slate-400 against #111827, which read as a
                      // cold grey-on-blue-black rather than as a neutral.
                      : "text-[#8a94a6] hover:bg-slate-700/70 hover:text-white"
                  )}
                  aria-current={isActive ? "page" : undefined}
                >
                  <Icon className="h-[19px] w-[19px]" />
                </a>
              </Link>
            );
          })}
        </nav>

        {/* Bottom section — the theme toggle only. Sign-out already lives in the
            header's avatar menu, where a destructive action belongs beside the
            account it applies to rather than as an unlabelled icon. */}
        <div className="flex flex-col items-center pb-4 pt-3">
          <button
            onClick={toggleTheme}
            title="Toggle theme"
            className="w-[38px] h-[38px] flex items-center justify-center rounded-[10px] text-[#8a94a6] hover:bg-slate-700/70 hover:text-white transition-colors"
          >
            {theme === 'light' ? <Moon className="h-[19px] w-[19px]" /> : <Sun className="h-[19px] w-[19px]" />}
          </button>
        </div>
      </div>
    </aside>
  );
}
